/**
 * Deterministic wheel-fitment compatibility check.
 *
 * Inputs:
 *   listingPublicData — fields from a listing (numberOfBolts, boltCircle,
 *     hubHole, diameter, tireWidthFront, profileFront, etFront).
 *   licensePlateInfo  — payload from getLicensePlateInfo. Two shapes
 *     are supported: { data: [car] } (fitment DB) and { result: car }
 *     (legacy car.info), matching how CompareSection reads it.
 *
 * Output: { compatible, confidence, summary, carSpecs[] } — same shape
 * as the old AiTryOn response so the result can be rendered in the
 * same look-and-feel.
 */

import { boltCircleMm } from './boltCircleCore';

const toNumber = (v) => {
  if (v == null || v === '') return null;
  // Tolerate ',' and '_' as decimal separators — listing inputs occasionally have these typos.
  const n = parseFloat(String(v).replace(/[,_]/g, '.'));
  return Number.isFinite(n) ? n : null;
};

const extractCar = (info) => {
  if (!info) return null;
  if (Array.isArray(info.data) && info.data.length > 0) return info.data[0];
  if (info.result) return info.result;
  return null;
};

const carSpecs = (car) => {
  const t = car?.technical || {};
  const tireInfo = car?.tire_info || {};
  return {
    bolts: toNumber(t.stud_holes ?? tireInfo.bolts),
    pcd: toNumber(t.pcd ?? tireInfo.bolt_circle),
    centreBore: toNumber(t.centre_bore ?? tireInfo.center_bore),
  };
};

// Pull rim diameter from strings like "7.5x17 ET40", "7.5Jx18", or just "18".
// Filter out junk values — car wheels are realistically 12-26 inches.
const parseRimDiameter = (rimStr) => {
  if (!rimStr) return null;
  let n = null;
  const m = String(rimStr).match(/x(\d+(?:\.\d+)?)/i);
  if (m) n = toNumber(m[1]);
  else n = toNumber(rimStr);
  return n != null && n >= 12 && n <= 26 ? n : null;
};

// Tire dim strings look like "235/65R17" (sometimes with load index after).
const TIRE_DIM_PATTERN = /^\d{3}\/\d{2}R\d{2}/i;
const isValidTireDim = (s) => s && TIRE_DIM_PATTERN.test(String(s).trim());

const parseRimEt = (rimStr) => {
  if (!rimStr) return null;
  const m = String(rimStr).match(/ET\s*(-?\d+(?:\.\d+)?)/i);
  return m ? toNumber(m[1]) : null;
};

const collectRims = (car) => {
  const rims = new Set();
  const ets = new Set();
  const push = (rim) => {
    if (!rim) return;
    const d = parseRimDiameter(rim);
    if (d != null) rims.add(d);
    const e = parseRimEt(rim);
    if (e != null) ets.add(e);
  };

  if (Array.isArray(car?.wheels)) {
    car.wheels.forEach((w) => {
      push(w?.front?.rim);
      push(w?.rear?.rim);
      if (toNumber(w?.front?.rim_offset) != null) ets.add(toNumber(w.front.rim_offset));
      if (toNumber(w?.rear?.rim_offset) != null) ets.add(toNumber(w.rear.rim_offset));
    });
  }
  if (car?.tire_info?.tires) {
    car.tire_info.tires.forEach((t) => {
      push(t?.front?.rim);
      push(t?.rear?.rim);
    });
  }
  push(car?.original_tires?.front_rims);
  push(car?.original_tires?.rear_rims);
  /* P25009-489 — an enriched car states every diameter it can use, already
   * cut at its minimum wheel size. The rim strings above only cover the
   * diameters the master sheet had a rim width for, so a 21" alternative size
   * would otherwise read as "wrong diameter". */
  if (Array.isArray(car?.diameters)) {
    car.diameters.forEach(diameter => {
      const d = toNumber(diameter);
      if (d != null) rims.add(d);
    });
  }
  return { rims: [...rims], ets: [...ets] };
};

const collectTires = (car) => {
  const tires = new Set();
  const add = (t) => {
    const cleaned = String(t || '').trim();
    if (isValidTireDim(cleaned)) {
      // Strip load/speed suffix like " 102H" so display is consistent.
      const match = cleaned.match(/^(\d{3}\/\d{2}R\d{2}(?:\.\d)?)/i);
      tires.add(match ? match[1] : cleaned);
    }
  };
  if (car?.tire_info?.tires) {
    car.tire_info.tires.forEach((t) => {
      add(t?.front?.tire);
      add(t?.rear?.tire);
    });
  }
  if (Array.isArray(car?.wheels)) {
    car.wheels.forEach((w) => {
      add(w?.front?.tire);
      add(w?.rear?.tire);
    });
  }
  add(car?.original_tires?.front_tires);
  add(car?.original_tires?.rear_tires);
  return [...tires];
};

const normalizeTireString = (s) =>
  String(s || '')
    .toUpperCase()
    .replace(/\s/g, '')
    .replace(/\.00/g, '');

const carDisplayName = (car) => {
  if (!car) return '';
  const brand = car.brand || car?.make?.name || '';
  const model =
    car?.series?.name ||
    (typeof car.series === 'string' ? car.series : '') ||
    car?.model?.name ||
    car?.model ||
    '';
  const year = car?.model_year || car?.start_year || '';
  return [brand, model, year].filter(Boolean).join(' ').trim();
};

const STATUS = { MATCH: 'match', MISMATCH: 'mismatch', UNCERTAIN: 'uncertain' };

/* P25009-482 BR-12 — how far below the car's hub the rim's centre bore may be
 * and still count as fitting. Bores are rounded to a tenth in the data: every
 * Volvo package lists a 63.3 bore against Volvo's 63.4 hub, and without the
 * tenth of slack the fit check told a Volvo buyer the Volvo wheels did not
 * fit. Same figure as server/api-util/fitment/carsThatFit.js, so the buy box
 * and "Passar även" agree. */
export const BORE_TOLERANCE_MM = 0.1;

/** Does a rim bore go on this hub? Compared in hundredths so float noise in
 * 63.4 - 0.1 cannot flip the answer. */
export const boreFitsHub = (rimBore, hubBore) =>
  Math.round((hubBore - rimBore) * 100) <= Math.round(BORE_TOLERANCE_MM * 100);

const TIRE_LISTING_TYPES = ['tire', 'businesstire'];
/* A tyre has no bolts and no bore, so those rows say nothing about it — and an
 * "uncertain" row would keep a tyre that fits from ever being confirmed. */
const isTireListing = listing =>
  TIRE_LISTING_TYPES.includes(listing.adType) || TIRE_LISTING_TYPES.includes(listing.listingType);


const compareBolts = (listing, car) => {
  if (isTireListing(listing)) return null;
  const listingBolts = toNumber(listing.numberOfBolts);
  if (listingBolts == null || car.bolts == null) {
    return { label: 'Bultmönster (antal)', value: car.bolts ? String(car.bolts) : '—', status: STATUS.UNCERTAIN };
  }
  return {
    label: 'Bultmönster (antal)',
    value: String(car.bolts),
    status: listingBolts === car.bolts ? STATUS.MATCH : STATUS.MISMATCH,
  };
};

/* boltCircleMm, not toNumber: Console's key for 139.7 is `1397`, which the
 * wizard and every import store (P25009-488), and read as a number it never
 * matched any car — a false "Passar inte". */
const comparePcd = (listing, car) => {
  if (isTireListing(listing)) return null;
  const listingPcd = boltCircleMm(listing.boltCircle);
  if (listingPcd == null || car.pcd == null) {
    return { label: 'PCD', value: car.pcd ? `${car.pcd} mm` : '—', status: STATUS.UNCERTAIN };
  }
  return {
    label: 'PCD',
    value: `${car.pcd} mm`,
    status: Math.abs(listingPcd - car.pcd) < 0.5 ? STATUS.MATCH : STATUS.MISMATCH,
  };
};

/* Only when both sides state a bore: most rim listings have no hub bore yet,
 * and an unknown must not turn a match into "uncertain" (the row is skipped,
 * like the tyre row on a rim). A wider bore is fine with a centring ring. */
const compareBore = (listing, car) => {
  if (isTireListing(listing)) return null;
  const rimBore = toNumber(listing.hubHole);
  if (rimBore == null || car.centreBore == null) return null;
  return {
    label: 'Navhål',
    value: `${car.centreBore} mm`,
    status: boreFitsHub(rimBore, car.centreBore) ? STATUS.MATCH : STATUS.MISMATCH,
  };
};

const compareDiameter = (listing, rims) => {
  const listingDiameter = toNumber(listing.diameter);
  if (listingDiameter == null) {
    return { label: 'Fälgdiameter', value: rims.length ? `${rims.join(', ')}"` : '—', status: STATUS.UNCERTAIN };
  }
  if (!rims.length) {
    return { label: 'Fälgdiameter', value: '—', status: STATUS.UNCERTAIN };
  }
  const ok = rims.some((d) => Math.abs(d - listingDiameter) < 0.5);
  return {
    label: 'Fälgdiameter',
    value: `${rims.join(', ')}"`,
    status: ok ? STATUS.MATCH : STATUS.MISMATCH,
  };
};

const TIRE_ROW_LABEL = 'Däckdimension';

const compareTire = (listing, tires) => {
  const { tireWidthFront, profileFront, diameter } = listing;
  if (!(tireWidthFront && profileFront && diameter)) {
    return null; // skip — this listing doesn't carry tire dims
  }
  const listingTire = normalizeTireString(`${tireWidthFront}/${profileFront}R${diameter}`);
  if (!tires.length) {
    return { label: TIRE_ROW_LABEL, value: '—', status: STATUS.UNCERTAIN };
  }
  const ok = tires.some((t) => normalizeTireString(t) === listingTire);
  return {
    label: TIRE_ROW_LABEL,
    value: tires.slice(0, 3).join(', '),
    status: ok ? STATUS.MATCH : STATUS.MISMATCH,
  };
};

/* P25009-492 — a "close" tyre size: what a car search falls back to when no
 * listing has one of the car's original sizes (the client's rule: exact sizes
 * first, close ones only when there are none). Close means the same rim
 * diameter, a tyre at most 10 mm wider or narrower, and a rolling diameter
 * within ±3 % of an original size. The ±3 % is the same figure "Passar även"
 * uses (server/api-util/fitment/carsThatFit.js); unlike there, the width may
 * differ by one step, or a 235/60R18 on a car that lists 225/60R18 would never
 * count. */
export const CLOSE_TIRE_WIDTH_MM = 10;
export const CLOSE_TIRE_DIAMETER_TOLERANCE = 0.03;
const MM_PER_INCH = 25.4;

const parseTireSize = (s) => {
  const m = /^(\d{3})\/(\d{2})Z?R(\d{2}(?:\.\d)?)/.exec(normalizeTireString(s));
  return m ? { width: Number(m[1]), profile: Number(m[2]), diameter: Number(m[3]) } : null;
};

const overallDiameterMm = (t) => (2 * t.width * t.profile) / 100 + t.diameter * MM_PER_INCH;

/** Is this tyre close to one of the car's sizes (see CLOSE_TIRE_* above)? */
export const isCloseTireSize = (tire, carTires = []) => {
  const listingTire = parseTireSize(tire);
  if (!listingTire) return false;
  return carTires.some((carTire) => {
    const size = parseTireSize(carTire);
    if (!size || Math.abs(size.diameter - listingTire.diameter) >= 0.5) return false;
    if (Math.abs(size.width - listingTire.width) > CLOSE_TIRE_WIDTH_MM) return false;
    const ratio = overallDiameterMm(listingTire) / overallDiameterMm(size);
    return Math.abs(ratio - 1) <= CLOSE_TIRE_DIAMETER_TOLERANCE;
  });
};

const summarize = (carName, specs, hadSeriesSelected) => {
  const mismatches = specs.filter((s) => s.status === STATUS.MISMATCH);
  const uncertains = specs.filter((s) => s.status === STATUS.UNCERTAIN);
  const matches = specs.filter((s) => s.status === STATUS.MATCH);

  if (mismatches.length > 0) {
    const labels = mismatches.map((s) => s.label.toLowerCase()).join(', ');
    return {
      compatible: false,
      confidence: 'high',
      summary: `Passar troligen inte din ${carName || 'bil'}: avvikelse i ${labels}.`,
    };
  }
  if (matches.length > 0 && uncertains.length === 0) {
    return {
      compatible: true,
      confidence: hadSeriesSelected ? 'high' : 'medium',
      summary: hadSeriesSelected
        ? `Hjulen passar din ${carName || 'bil'} enligt specifikationerna.`
        : `Hjulen ser ut att passa din ${carName || 'bil'}. För säkrare kontroll, välj rätt motor/serie i registreringsverktyget.`,
    };
  }
  return {
    compatible: null,
    confidence: 'low',
    summary: hadSeriesSelected
      ? 'Otillräcklig data för säker bedömning. Kontrollera specifikationerna manuellt.'
      : 'Osäker matchning – välj rätt motor/serie i registreringsverktyget för en mer exakt kontroll.',
  };
};

export const checkWheelFitment = ({ listingPublicData, licensePlateInfo, hasSeries = false }) => {
  const car = extractCar(licensePlateInfo);
  if (!car) return null;

  const specs = carSpecs(car);
  const { rims } = collectRims(car);
  const tires = collectTires(car);

  const rows = [
    compareBolts(listingPublicData, specs),
    comparePcd(listingPublicData, specs),
    compareBore(listingPublicData, specs),
    compareDiameter(listingPublicData, rims),
    compareTire(listingPublicData, tires),
  ].filter(Boolean);

  const name = carDisplayName(car);
  const verdict = summarize(name, rows, hasSeries);

  return {
    carDetected: name || 'Din bil',
    carSpecs: rows,
    ...verdict,
  };
};

export const FITMENT_STATUS = STATUS;

/* How well a listing fits the car, for the car search (P25009-492). */
export const FIT_LEVEL = { EXACT: 'exact', CLOSE: 'close', NO: 'no', UNKNOWN: 'unknown' };

/**
 * EXACT when the fit check passes ("✓ Passar"). CLOSE when the only thing it
 * objects to is the tyre size and that size is close to one of the car's
 * (isCloseTireSize) — bolt pattern, bore and rim diameter must all match.
 * UNKNOWN when the check cannot tell, NO otherwise. The buy box keeps using
 * checkWheelFitment: a close size is never called a fit there.
 */
export const wheelFitLevel = ({ listingPublicData, licensePlateInfo }) => {
  const verdict = checkWheelFitment({ listingPublicData, licensePlateInfo });
  if (!verdict) return FIT_LEVEL.UNKNOWN;
  if (verdict.compatible === true) return FIT_LEVEL.EXACT;
  if (verdict.compatible !== false) return FIT_LEVEL.UNKNOWN;

  const tireRow = verdict.carSpecs.find((s) => s.label === TIRE_ROW_LABEL);
  const otherRows = verdict.carSpecs.filter((s) => s.label !== TIRE_ROW_LABEL);
  const onlyTireIsOff =
    tireRow?.status === STATUS.MISMATCH && otherRows.every((s) => s.status === STATUS.MATCH);
  if (!onlyTireIsOff) return FIT_LEVEL.NO;

  const { tireWidthFront, profileFront, diameter } = listingPublicData || {};
  const tires = collectTires(extractCar(licensePlateInfo));
  return isCloseTireSize(`${tireWidthFront}/${profileFront}R${diameter}`, tires)
    ? FIT_LEVEL.CLOSE
    : FIT_LEVEL.NO;
};

/**
 * Every wheel diameter the car runs, smallest first ([18, 19, 20, 21, 22]),
 * from its rims and its tyre sizes. Takes the car object itself, not the
 * plate payload. The car search filters on these so a 16" wheel never comes
 * back for a car whose smallest wheel is 18" (P25009-492).
 */
export const wheelDiametersOfCar = (car) => {
  if (!car) return [];
  const diameters = new Set(collectRims(car).rims.map((d) => Math.round(d)));
  collectTires(car).forEach((t) => {
    const size = parseTireSize(t);
    if (size && size.diameter >= 12 && size.diameter <= 26) diameters.add(Math.round(size.diameter));
  });
  return [...diameters].sort((a, b) => a - b);
};

/** The car inside a plate-lookup payload, or null. */
export const plateInfoCar = extractCar;

/** "Volvo V90 2019" from a plate-lookup payload, or ''. */
export const plateInfoCarName = info => carDisplayName(extractCar(info));

/**
 * P25009-489 BR-5 — does the looked-up car need TPMS sensors, and which?
 *
 * Only an enriched car database row says (SpecialWheels' sensor model, e.g.
 * "RF433"); a car.info-only answer or a payload cached in the browser before
 * the enrichment has no `tpms`, and then nothing is shown rather than a guess.
 *
 * @returns {{required: boolean, sensorLabel: string}|null}
 */
export const plateInfoTpms = info => {
  const tpms = extractCar(info)?.tpms;
  if (!tpms || typeof tpms !== 'object') return null;
  return {
    required: !!tpms.required,
    sensorLabel: String(tpms.sensor_label || tpms.sensor_model || '').trim(),
  };
};

/** "TPMS krävs · Sensor: RF433 MHz" — or '' when the car does not need TPMS. */
export const tpmsRequirementText = info => {
  const tpms = plateInfoTpms(info);
  if (!tpms?.required) return '';
  return tpms.sensorLabel ? `TPMS krävs · Sensor: ${tpms.sensorLabel}` : 'TPMS krävs';
};

/** The model alone ("V90"), for copy like "Andra vinterhjul till din V90". */
export const plateInfoModelName = info => {
  const car = extractCar(info);
  if (!car) return '';
  return (
    car?.series?.name ||
    (typeof car.series === 'string' ? car.series : '') ||
    car?.model?.name ||
    (typeof car.model === 'string' ? car.model : '') ||
    ''
  );
};
